import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, BadgeCheck, Briefcase, Signal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SiteHeader } from "@/components/landing/SiteHeader";
import { SiteFooter } from "@/components/landing/Footer";
import { Pricing } from "@/components/landing/Pricing";
import { HashLink } from "@/components/landing/HashLink";
import { DashboardPreview, Faq, FeaturedCourses, WhoItsFor } from "@/components/landing/Sections";
import { HowItWorks } from "@/components/landing/HowItWorks";
import { SiteBanner, useSiteContent } from "@/pages/SitePages";

const HERO = "https://images.unsplash.com/photo-1604933762021-54a5858c9832?w=1000&q=55&fm=webp&fit=crop";

export default function Landing() {
  const { hash } = useLocation();
  const seo = useSiteContent("home");
  useEffect(() => { if (seo?.seo_title) document.title = seo.seo_title; }, [seo]);
  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" });
  }, [hash]);
  return (
    <div className="min-h-screen bg-white">
      <SiteBanner />
      <SiteHeader />

      <section className="grid-paper border-b border-slate-200">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 py-14 sm:px-6 lg:grid-cols-12 lg:py-24">
          <div className="lg:col-span-7">
            <p className="rise font-mono-label mb-5 inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-1 text-xs uppercase tracking-[0.18em] text-slate-700">
              <Signal className="h-3.5 w-3.5 text-[#222d99]" /> Career readiness, built for low data
            </p>
            <h1 className="rise rise-1 text-4xl font-extrabold leading-[1.05] tracking-tight text-slate-950 sm:text-5xl lg:text-6xl">
              Get job ready.<br /><span className="text-[#222d99]">Prove it</span> with real skills.
            </h1>
            <p className="rise rise-2 mt-6 max-w-xl text-base text-slate-700 sm:text-lg">
              Crowlearn helps job seekers, graduates and career changers learn the skills employers ask for — and helps employers find candidates whose skills are verified.
            </p>
            <div className="rise rise-3 mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="rounded-full bg-[#222d99] px-7 hover:bg-[#1a237e]" data-testid="hero-learner-cta">
                <Link to="/sign-up">Start learning free <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full border-slate-900 px-7" data-testid="hero-organisations-cta">
                <HashLink hash="#organisations"><Briefcase className="mr-1 h-4 w-4" /> For Organisations</HashLink>
              </Button>
            </div>
          </div>
          <div className="relative lg:col-span-5">
            <img src={HERO} alt="Learner studying on a laptop" width="1000" height="1100" loading="eager"
              className="rise rise-2 aspect-[4/5] w-full rounded-2xl border border-slate-200 object-cover" />
            <div className="rise rise-3 absolute -bottom-5 left-4 right-4 rounded-xl border border-slate-200 bg-white/90 p-4 shadow-lg backdrop-blur-md sm:left-8 sm:right-auto sm:w-72">
              <p className="flex items-center gap-2 text-sm font-semibold text-slate-900"><BadgeCheck className="h-4 w-4 text-emerald-600" /> Customer Service</p>
              <p className="mt-1 text-xs text-slate-700">Verified by course completion and quiz score</p>
            </div>
          </div>
        </div>
      </section>

      <FeaturedCourses />

      <HowItWorks />

      <WhoItsFor />
      <DashboardPreview />
      <Pricing />
      <Faq />

      <section id="employers" className="scroll-mt-20 bg-[#090A0F] text-white">
        <div className="mx-auto grid max-w-6xl gap-4 px-4 py-14 sm:px-6 md:grid-cols-2">
          <div className="flex flex-col justify-between gap-6 rounded-2xl border border-white/15 p-6 sm:p-8">
            <div>
              <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">Hiring? Meet candidates with verified skills.</h2>
              <p className="mt-2 max-w-xl text-sm text-slate-200">Employer accounts are reviewed by our team before candidate profiles become visible.</p>
            </div>
            <Button asChild size="lg" className="w-fit rounded-full bg-white px-7 text-slate-950 hover:bg-slate-200" data-testid="employer-band-cta">
              <Link to="/sign-up">Create employer account</Link>
            </Button>
          </div>
          <div className="flex flex-col justify-between gap-6 rounded-2xl bg-[#222d99] p-6 sm:p-8">
            <div>
              <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">Looking for work? Build your path.</h2>
              <p className="mt-2 max-w-xl text-sm text-indigo-50">Tell us your target role and start learning the skills that get you there.</p>
            </div>
            <Button asChild size="lg" className="w-fit rounded-full bg-white px-7 text-[#222d99] hover:bg-indigo-50" data-testid="learner-band-cta">
              <Link to="/sign-up">Start your path <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
